import { Encoding } from '@mode-vegas/vegas';

import { EncodingHelpers, lookupYField, RenderDataModel } from '@mode/charts/util-vegas';
import { FlamingoTypes } from '@mode/shared/contract-common';
import { Switch } from '@mode/shared/util-switch';

export interface ChartTitles {
  xAxis?: string;
  yAxis?: string;
  y2Axis?: string;
  legend?: string;
}

export namespace ChartTitle {
  export function makeComboTitles(model: RenderDataModel, encoding: Encoding): ChartTitles {
    encoding = EncodingHelpers.ensureVerticalEncoding(encoding);
    const xField = model.makeField(encoding.x[0]);

    const titles: ChartTitles = {};
    encoding.marks.series.forEach((series, index) => {
      const yEncField = lookupYField(encoding, series.field);
      const yField = model.makeField(yEncField);

      if (index === 0) {
        titles.yAxis = yField.name;
      } else {
        titles.y2Axis = yField.name;
      }
    });

    return {
      ...titles,
      xAxis: (xField && xField.name) || null,
      legend: null,
    };
  }

  export function makeGroupedTitles(model: RenderDataModel, encoding: Encoding): ChartTitles {
    encoding = EncodingHelpers.ensureVerticalEncoding(encoding);
    const xField = model.makeField(encoding.x[0]);
    const groupField = model.makeField(encoding.column[0]);

    let colorField: FlamingoTypes.ModeField;
    if (encoding.marks.series[0].properties.color.values.length) {
      colorField = model.makeField(encoding.marks.series[0].properties.color.values[0]);
    }

    const valueFields = encoding.value.map((encField) => model.makeField(encField));

    return {
      yAxis: encoding.value.length === 1 ? valueFields[0].name : 'Values',
      xAxis: Switch.Fields.isNames(xField) ? groupField.name : (xField && xField.name) || null,
      legend: colorField == null ? null : Switch.Fields.isNames(colorField) ? null : colorField.name,
    };
  }

  export function makeStackedTitles(model: RenderDataModel, encoding: Encoding): ChartTitles {
    encoding = EncodingHelpers.ensureVerticalEncoding(encoding);
    const xField = model.makeField(encoding.x[0]);

    let colorField: FlamingoTypes.ModeField;
    if (encoding.marks.series[0].properties.color.values.length) {
      colorField = model.makeField(encoding.marks.series[0].properties.color.values[0]);
    }

    const valueFields = encoding.value.map((encField) => model.makeField(encField));

    return {
      yAxis: encoding.value.length == 1 ? valueFields[0].name : 'Values',
      xAxis: Switch.Fields.isNames(xField) ? null : (xField && xField.name) || null,
      legend: colorField == null ? null : Switch.Fields.isNames(colorField) ? null : colorField.name,
    };
  }

  export function makeScatterTitles(model: RenderDataModel, encoding: Encoding): ChartTitles {
    encoding = EncodingHelpers.ensureVerticalEncoding(encoding);
    const xField = model.makeField(encoding.x[0]);

    let colorField: FlamingoTypes.ModeField;
    if (encoding.marks.series[0].properties.color.values.length) {
      colorField = model.makeField(encoding.marks.series[0].properties.color.values[0]);
    }

    const valueFields = encoding.value.map((encField) => model.makeField(encField));

    return {
      yAxis: encoding.value.length === 1 ? valueFields[0].name : 'Values',
      xAxis: Switch.Fields.isNames(xField) ? null : (xField && xField.name) || null,
      legend: colorField == null ? null : Switch.Fields.isNames(colorField) ? null : colorField.name,
    };
  }

  export function makePieTitles(model: RenderDataModel, encoding: Encoding): ChartTitles {
    encoding = EncodingHelpers.ensureVerticalEncoding(encoding);
    const colorFieldEnc = encoding.marks.properties.color.values[0];
    const angleFieldEnc = encoding.marks.properties.angle.values[0];

    const colorField = model.buildField(colorFieldEnc.formula, colorFieldEnc.variable.type, colorFieldEnc.id);
    const angleField = model.buildField(angleFieldEnc.formula, angleFieldEnc.variable.type, angleFieldEnc.id);

    return {
      legend: (colorField && colorField.name) || null,
      xAxis: (angleField && angleField.name) || null,
    };
  }

  export function makeHistogramTitles(model: RenderDataModel, encoding: Encoding): ChartTitles {
    encoding = EncodingHelpers.ensureVerticalEncoding(encoding);
    const xField = model.makeField(encoding.x[0]);
    const yField = model.makeField(encoding.y[0]);

    const xCompiled = model.compileField(xField);
    const yCompiled = model.compileField(yField);

    return {
      yAxis: (yCompiled && yCompiled.displayFormula) || null,
      xAxis: (xCompiled && xCompiled.displayFormula) || null,
      legend: null,
    };
  }
}
